import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import {
  usePatientAppointments,
  type AppointmentStatus,
  type PatientAppointmentItem,
} from '@/hooks/usePatientAppointments';
import { useTreatmentCoursesForPatient } from '@/hooks/useTreatmentCourses';
import {
  courseContextByAppointmentId,
  formatAppointmentCourseContextLine,
} from '@/lib/appointmentCourseContext';
import {
  appointmentCourseStepPath,
  writeTreatmentCoursePatientParams,
} from '@/lib/treatmentCoursePatientParams';
import {
  appointmentIsListedUpcoming,
  appointmentIsOnClinicDay,
  appointmentListFilter,
  appointmentStatusLabelKey,
  resolveAppointmentStatus,
  type AppointmentLifecycleStatus,
  type ResolveAppointmentStatusInput,
} from '@/utils/appointmentStatus';
import { AppointmentModal } from '@/pages/appointments/AppointmentModal';
import { AppointmentDetailsModal } from '@/pages/appointments/AppointmentDetailsModal';
import { BookTreatmentCourseSlideOver } from '@/components/appointments/BookTreatmentCourseSlideOver';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { DateBlock } from '@/components/ui/DateBlock';
import { FilterChip, PaymentChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { buildAppointmentInvoiceHref } from '@/hooks/useAppointmentInvoicing';
import { PatientTabFrame, PatientTabToolbar } from '@/components/patient/PatientTabFrame';

function statusInput(apt: PatientAppointmentItem, now: Date): ResolveAppointmentStatusInput {
  return {
    status: apt.status,
    visitStatus: apt.visit_status,
    startsAt: apt.starts_at,
    endsAt: apt.ends_at,
    now,
  };
}

interface AppointmentTabProps {
  patientId: string;
  clinicId: string;
}

export function AppointmentTab({ patientId, clinicId }: AppointmentTabProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: appointments, isLoading, error, refetch } = usePatientAppointments(patientId);
  const { data: courses } = useTreatmentCoursesForPatient(clinicId, patientId);
  const courseByAppointmentId = useMemo(
    () => courseContextByAppointmentId(courses ?? []),
    [courses],
  );
  const [showNewModal, setShowNewModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');
  const dateLocale = i18n.language === 'da' ? da : enUS;
  const now = new Date();
  const list = appointments ?? [];
  const counts = {
    all: list.length,
    upcoming: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0,
  };
  for (const apt of list) {
    const filter = appointmentListFilter(statusInput(apt, now));
    if (filter) counts[filter] += 1;
  }

  const filtered = list.filter(
    (apt) =>
      statusFilter === 'all' || appointmentListFilter(statusInput(apt, now)) === statusFilter,
  );

  const upcoming = filtered.filter((apt) => appointmentIsListedUpcoming(statusInput(apt, now)));
  const today = upcoming.filter((apt) => appointmentIsOnClinicDay(apt.starts_at, now));
  const later = upcoming.filter((apt) => !appointmentIsOnClinicDay(apt.starts_at, now));
  const past = filtered.filter((apt) => !appointmentIsListedUpcoming(statusInput(apt, now)));

  const handleAppointmentClick = (apt: PatientAppointmentItem) => {
    setDetailsId(apt.id);
  };

  const getStatusTone = (status: AppointmentLifecycleStatus): StatusTone => {
    switch (status) {
      case 'upcoming':
        return 'upcoming';
      case 'checked_in':
      case 'under_treatment':
        return 'draft';
      case 'treatment_finished':
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      case 'no_show':
        return 'no_show';
      default: {
        const _exhaustive: never = status;
        return _exhaustive;
      }
    }
  };

  const getPaymentMethodLabel = (method?: string | null) => {
    if (!method) return null;
    const key = method.trim().toLowerCase();
    const labels: Record<string, string> = {
      card: t('appointments.payment.method_card'),
      stripe: t('appointments.payment.method_card'),
      clinio_pay: t('appointments.payment.method_card'),
      kort: t('appointments.payment.method_card'),
      mobilepay: t('appointments.payment.method_mobilepay'),
      punch_card: t('appointments.payment.method_punch_card'),
      klippekort: t('appointments.payment.method_punch_card'),
      manual: t('appointments.payment.method_manual'),
      cash: t('appointments.payment.method_cash', 'Kontant'),
      kontant: t('appointments.payment.method_cash', 'Kontant'),
      bank: t('appointments.payment.method_bank', 'Bankoverførsel'),
      bankoverførsel: t('appointments.payment.method_bank', 'Bankoverførsel'),
      bankoverforsel: t('appointments.payment.method_bank', 'Bankoverførsel'),
      invoice: t('appointments.payment.method_invoice', 'Faktura'),
      faktura: t('appointments.payment.method_invoice', 'Faktura'),
    };
    return labels[key] ?? method;
  };

  const renderAppointmentRow = (apt: PatientAppointmentItem) => {
    const start = new Date(apt.starts_at);
    const end = new Date(apt.ends_at);
    const lifecycle = resolveAppointmentStatus({
      status: apt.status,
      visitStatus: apt.visit_status,
      startsAt: apt.starts_at,
      endsAt: apt.ends_at,
    });
    const rail = apt.status === 'cancelled' ? 'cancelled' : apt.notes ? 'draft' : 'draft';
    const methodLabel = getPaymentMethodLabel(apt.payment_method);
    const course = courseByAppointmentId.get(apt.id);
    const courseLine = course
      ? formatAppointmentCourseContextLine(course, {
          visibleTitle: apt.service_name,
          stepOfTotal: t('appointments.course_badge', {
            n: course.stepIndex,
            total: course.stepCount,
          }),
        })
      : null;
    const courseHref = course
      ? appointmentCourseStepPath({
          patientId,
          courseId: course.courseId,
          stepId: course.stepId,
        })
      : null;

    return (
      <ListRow
        key={apt.id}
        rail={rail}
        role="button"
        tabIndex={0}
        onClick={() => handleAppointmentClick(apt)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleAppointmentClick(apt);
          }
        }}
        className="w-full cursor-pointer text-left hover:bg-ds-sunk"
      >
        <DateBlock
          weekday={format(start, 'EEE', { locale: dateLocale })}
          day={format(start, 'd')}
          month={format(start, 'MMM', { locale: dateLocale })}
          today={appointmentIsOnClinicDay(apt.starts_at, now)}
        />
        <div className="min-w-0 flex-1">
          <p className={cn(TYPE.body, 'font-semibold text-ds-text')}>
            {apt.service_name ?? t('appointments.no_service')}
          </p>
          <p className={cn(TYPE.small, 'mt-s1 tabular-nums text-ds-muted')}>
            {format(start, 'HH:mm')}–{format(end, 'HH:mm')} · {t('appointments.practitioner')}:{' '}
            {apt.clinician_name ?? t('appointments.unassigned')}
          </p>
          {courseLine ? (
            <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
              {courseHref ? (
                <Link
                  to={courseHref}
                  data-testid="appointment-course-context"
                  className="rounded font-semibold text-ds-teal underline-offset-[3px] hover:underline focus-visible:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {courseLine}
                </Link>
              ) : (
                <span data-testid="appointment-course-context">{courseLine}</span>
              )}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-s2">
          <StatusChip tone={getStatusTone(lifecycle)}>
            {t(appointmentStatusLabelKey(lifecycle))}
          </StatusChip>
          {apt.payment_status === 'paid' ? (
            <PaymentChip>
              {t('appointments.payment.paid')}
              {methodLabel ? ` · ${methodLabel}` : ''}
            </PaymentChip>
          ) : apt.payment_status === 'pending' ? (
            <PaymentChip>{t('appointments.payment.pending')}</PaymentChip>
          ) : apt.invoice_id ? (
            <PaymentChip>{t('appointments.has_invoice')}</PaymentChip>
          ) : null}
          {apt.invoice_id ? (
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={(event) => {
                event.stopPropagation();
                navigate(
                  buildAppointmentInvoiceHref({
                    invoiceId: apt.invoice_id,
                    patientId: apt.patient_id,
                    clinicId: apt.clinic_id,
                    appointmentId: apt.id,
                    returnTo: `/patients/${apt.patient_id}`,
                  }),
                );
              }}
            >
              {apt.invoice_number
                ? t('appointments.invoice_link', { number: apt.invoice_number })
                : t('appointments.has_invoice')}
            </Button>
          ) : null}
        </div>
      </ListRow>
    );
  };

  const empty = !isLoading && !error && filtered.length === 0;

  return (
    <div className="flex flex-col gap-s4">
      <PatientTabFrame
        title={t('patients.tabs.appointments')}
        count={counts.all}
        action={
          <div className="flex flex-wrap items-center gap-s2">
            <Button type="button" onClick={() => setShowNewModal(true)}>
              {t('appointments.new_appointment')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowCourseModal(true)}>
              {t('treatment_courses.book_title')}
            </Button>
          </div>
        }
        loading={isLoading}
        error={error ? t('appointments.error_loading') : undefined}
        onRetry={() => void refetch()}
        toolbar={
          <PatientTabToolbar
            chips={
              <div className="flex gap-s2" aria-label={t('appointments.filter.all')}>
                {(
                  [
                    ['all', counts.all],
                    ['upcoming', counts.upcoming],
                    ['completed', counts.completed],
                    ['cancelled', counts.cancelled],
                    ['no_show', counts.no_show],
                  ] as const
                ).map(([key, count]) => (
                  <FilterChip
                    key={key}
                    pressed={statusFilter === key}
                    count={count}
                    onClick={() => setStatusFilter(key)}
                  >
                    {t(`appointments.filter.${key}`)}
                  </FilterChip>
                ))}
              </div>
            }
          />
        }
        empty={
          empty
            ? {
                icon: <Calendar className="h-7 w-7" />,
                title:
                  statusFilter === 'all'
                    ? t('appointments.no_appointments')
                    : t('appointments.no_appointments_filter'),
                description: t('patients.appointments_empty_hint'),
              }
            : undefined
        }
      >
        <div className="mt-s4">
          {today.length > 0 ? (
            <section>
              <h3 className={cn(TYPE.label, 'px-s4 py-s2 text-ds-muted')}>
                {t('appointments.today', 'I dag')}
                <span className="ml-s2 tabular-nums">{today.length}</span>
              </h3>
              {today.map(renderAppointmentRow)}
            </section>
          ) : null}
          {later.length > 0 ? (
            <section>
              <h3 className={cn(TYPE.label, 'px-s4 py-s2 text-ds-muted')}>
                {t('appointments.upcoming_title')}
                <span className="ml-s2 tabular-nums">{later.length}</span>
              </h3>
              {later.map(renderAppointmentRow)}
            </section>
          ) : null}
          {past.length > 0 ? (
            <section>
              <h3 className={cn(TYPE.label, 'px-s4 py-s2 text-ds-muted')}>
                {t('appointments.past_title')}
                <span className="ml-s2 tabular-nums">{past.length}</span>
              </h3>
              {past.map(renderAppointmentRow)}
            </section>
          ) : null}
        </div>
      </PatientTabFrame>

      {showNewModal && (
        <AppointmentModal
          clinicId={clinicId}
          mode="create"
          initialPatientId={patientId}
          onClose={() => setShowNewModal(false)}
        />
      )}

      {showCourseModal ? (
        <BookTreatmentCourseSlideOver
          clinicId={clinicId}
          initialPatientId={patientId}
          onClose={() => setShowCourseModal(false)}
          onCreated={(created) => {
            const params = writeTreatmentCoursePatientParams(searchParams, {
              courseId: created.id,
              stepId: null,
              edit: false,
              journal: false,
            });
            navigate({ search: `?${params.toString()}` });
          }}
        />
      ) : null}

      {detailsId && (
        <AppointmentDetailsModal
          appointmentId={detailsId}
          clinicId={clinicId}
          onClose={() => setDetailsId(null)}
          onEdit={() => {
            const appointmentId = detailsId;
            setDetailsId(null);
            setEditingId(appointmentId);
          }}
        />
      )}

      {editingId && (
        <AppointmentModal
          clinicId={clinicId}
          mode="edit"
          appointmentId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
